import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { ApolloError } from '@apollo/client';

import {
  CircularProgress,
  Button,
  Typography,
  EditIcon,
  VisibilityOutlinedIcon,
  ContentCopyOutlinedIcon,
  LinkItem,
} from '@occc/ui-design-system';
import {
  OfferSupportAlert,
  useAppLayoutContextConsumer,
  snackbar,
  NotFoundPage,
  Page,
} from '@occc-app/design-system';
import { getOfferComponentType } from '@occc-app/offerHelpers/utils';
import {
  ComponentDetails,
  ConstraintSupplementalData,
  Offer,
} from '@occc-app/routes/CampaignComponents/component-details';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { useCopyOffer } from '@occc-app/offerHelpers/hooks/useCopyOffer';
import { programManagementGameRouteCreator } from '@occc-app/routes/Game/utils';
import { CopyConfirmDialog } from '@occc-app/routes/CampaignComponents/components/copy-confirm-dialog.component';

interface GameDetailsPageProps {
  offer?: Offer;
  constraintSupplementalData?: ConstraintSupplementalData;
  loading: boolean;
  error?: ApolloError;
  links: LinkItem[];
}

export const GameDetailsPage = ({
  offer,
  constraintSupplementalData,
  loading,
  error,
  links,
}: GameDetailsPageProps) => {
  const navigate = useNavigate();

  const isUserPermittedToCreate = useIsUserPermittedTo(Feature.GAME_CREATE);
  const isUserPermittedToEdit = useIsUserPermittedTo(Feature.GAME_EDIT);
  const isUserPermittedToView = useIsUserPermittedTo(Feature.GAME_EDIT_VIEW_ONLY);

  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isCopyInProgress, setIsCopyInProgress] = useState(false);

  const copyOffer = useCopyOffer();
  const openCopyDialog = useCallback(() => setIsCopyDialogOpen(true), []);
  const closeCopyDialog = useCallback(() => setIsCopyDialogOpen(false), []);

  const onCopyConfirm = useCallback(async () => {
    if (!offer) {
      return;
    }

    if (!offer.earningMechanism) {
      snackbar.error(
        "This game cannot be copied because it doesn't have an earning mechanism configured",
      );
      return;
    }

    setIsCopyInProgress(true);
    const offerId = await copyOffer({ offer, constraints: offer.preparedConstraints });

    setIsCopyInProgress(false);
    closeCopyDialog();

    if (offerId) {
      navigate(programManagementGameRouteCreator.update(offerId));
    }
  }, [offer, copyOffer, closeCopyDialog, navigate]);

  useAppLayoutContextConsumer(links);

  if (error) {
    return <Typography color='error'>Something went wrong. Please try again.</Typography>;
  }

  if (loading && !offer) {
    return <CircularProgress />;
  }

  if (!offer) {
    return <NotFoundPage />;
  }

  if (!offer.engagementType) {
    return <Typography color='error'>Offer does not have engagementType</Typography>;
  }

  const isSupported = !!getOfferComponentType(offer);
  const isEditButtonVisible = isSupported && isUserPermittedToEdit;
  const isCopyButtonVisible = isSupported && isUserPermittedToCreate;
  const isViewButtonVisible = isSupported && !isUserPermittedToEdit && isUserPermittedToView;

  return (
    <Page
      browserTabTitle='Vouchered Game Details'
      pageHeaderProps={{
        title: offer.name,
        subtitle: offer.description || undefined,
      }}
      actions={
        <>
          {isEditButtonVisible && (
            <Button
              color='secondary'
              variant='contained'
              component={Link}
              to={programManagementGameRouteCreator.update(offer.id)}
              startIcon={<EditIcon />}
              role='button'
            >
              Edit
            </Button>
          )}
          {isCopyButtonVisible && (
            <Button
              color='secondary'
              variant='contained'
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={openCopyDialog}
            >
              Copy
            </Button>
          )}
          {isViewButtonVisible && (
            <Button
              color='secondary'
              variant='contained'
              component={Link}
              to={programManagementGameRouteCreator.update(offer.id)}
              startIcon={<VisibilityOutlinedIcon />}
              role='button'
            >
              View Configuration
            </Button>
          )}
        </>
      }
    >
      {!isSupported && (isUserPermittedToEdit || isUserPermittedToView) && (
        <Box mb={2}>
          <OfferSupportAlert
            title='Program management does not yet support this configuration.'
            fallbackLink={`/offers/${offer.id}`}
            fallbackLinkTitle='View the original offer'
          />
        </Box>
      )}

      <ComponentDetails offer={offer} constraintSupplementalData={constraintSupplementalData} />

      <CopyConfirmDialog
        componentName='Game'
        open={isCopyDialogOpen}
        loading={isCopyInProgress}
        onClose={closeCopyDialog}
        onConfirm={onCopyConfirm}
      />
    </Page>
  );
};
